// Runkeeper-style Lock Screen + Dynamic Island Live Activity for outdoor runs.
// Wraps the native `StraivLiveActivity` Capacitor plugin (iOS only, 16.2+).
// All calls are no-ops on web and will not throw — making it safe to call from
// shared React code.

import { Capacitor, registerPlugin } from "@capacitor/core";

interface RunLiveActivityState {
  elapsedSeconds: number;
  distanceMeters: number;
  paceSecondsPerKm: number;
  isPaused: boolean;
}

interface StraivLiveActivityPlugin {
  startActivity(): Promise<void>;
  updateActivity(state: RunLiveActivityState): Promise<void>;
  endActivity(): Promise<void>;
}

const StraivLiveActivity =
  registerPlugin<StraivLiveActivityPlugin>("StraivLiveActivity");

const isAvailable = (): boolean => {
  if (!Capacitor.isNativePlatform()) return false;
  return Capacitor.getPlatform() === "ios";
};

let activityActive = false;
let lastUpdateMs = 0;

const safeNumber = (val: unknown, fallback = 0): number => {
  const n = typeof val === "number" ? val : Number(val);
  return Number.isFinite(n) && n >= 0 ? n : fallback;
};

export const RunLiveActivity = {
  isSupported: isAvailable,

  isActive: () => activityActive,

  /**
   * Start the Lock Screen + Dynamic Island activity. Safe to call multiple
   * times — subsequent calls are ignored if an activity is already running.
   */
  async start(): Promise<void> {
    if (!isAvailable()) return;
    if (activityActive) return;
    try {
      await StraivLiveActivity.startActivity();
      activityActive = true;
      lastUpdateMs = 0;
      console.log("[LiveActivity] started");
    } catch (err) {
      // Most common reason: user disabled Live Activities in Settings.
      console.warn("[LiveActivity] start failed:", err);
    }
  },

  /**
   * Push the current run stats into the Lock Screen widget. Throttles updates
   * to roughly once per second to avoid hitting iOS's update budget.
   */
  async update(state: Partial<RunLiveActivityState>): Promise<void> {
    if (!isAvailable()) return;
    if (!activityActive) return;

    const now = Date.now();
    if (now - lastUpdateMs < 900) return;
    lastUpdateMs = now;

    try {
      await StraivLiveActivity.updateActivity({
        elapsedSeconds: Math.round(safeNumber(state.elapsedSeconds)),
        distanceMeters: safeNumber(state.distanceMeters),
        paceSecondsPerKm: safeNumber(state.paceSecondsPerKm),
        isPaused: !!state.isPaused,
      });
    } catch (err) {
      console.warn("[LiveActivity] update failed:", err);
    }
  },

  /**
   * Force-flush an update, bypassing the throttle. Use when the run state
   * changes meaningfully (pause / resume / finish).
   */
  async flush(state: Partial<RunLiveActivityState>): Promise<void> {
    if (!isAvailable()) return;
    if (!activityActive) return;
    lastUpdateMs = 0;
    await this.update(state);
  },

  /**
   * Tear the activity down. Always safe to call (even if already ended).
   */
  async end(): Promise<void> {
    if (!isAvailable()) return;
    if (!activityActive) return;
    try {
      await StraivLiveActivity.endActivity();
    } catch (err) {
      console.warn("[LiveActivity] end failed:", err);
    } finally {
      activityActive = false;
      lastUpdateMs = 0;
      console.log("[LiveActivity] ended");
    }
  },
};

export type { RunLiveActivityState };
